import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Send } from "lucide-react";
import { LOGO_URL, TELEGRAM_URL } from "@/lib/api";

const HERO_BG =
  "https://images.unsplash.com/photo-1682220784379-e0ce4380db64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxkYXJRJTIwYWJzdHJhY3QlMjBnbG93aW5nJTIwb3JhbmdlJTIwbmVvbiUyMGxpbmVzJTIwY2luZW1hJTIwc3RyZWFtaW5nfGVufDB8fHx8MTc5MjI0ODUxN3ww&ixlib=rb-4.1.0&q=85";

const LINES = ["POPULAR IPTV APPS", "REBRANDED WITH", "YOUR LOGO"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="w-full h-[120%] object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080C]/70 via-[#08080C]/55 to-[#08080C]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#08080C_85%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <motion.img
          src={LOGO_URL}
          alt="Cowboy Apps"
          data-testid="hero-logo"
          className="h-24 sm:h-28 w-auto mb-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        />

        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#FF5500] mb-6" data-testid="hero-kicker">
          Saddle up — the apps you love, wearing your brand
        </p>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "text-[#FF5500]" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.14, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base sm:text-lg text-[#9E9EB3] leading-relaxed"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          I take the best-known IPTV apps and rebrand them with your logo, your name and
          your colors — so your customers download an app that looks 100% yours.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <button
            data-testid="hero-browse-button"
            onClick={() => document.getElementById("apps")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-brand inline-flex items-center gap-2 bg-[#FF5500] text-[#08080C] font-semibold px-7 py-3.5 rounded-full"
          >
            Browse the apps <ArrowDown className="w-4 h-4" />
          </button>
          <a
            data-testid="hero-telegram-button"
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[#282836] text-[#F4F4F8] px-7 py-3.5 rounded-full hover:border-[#FF5500] hover:text-white transition-colors"
          >
            <Send className="w-4 h-4 text-[#FF5500]" /> Order on Telegram
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#63637A]">
        Scroll to ride
      </div>
    </section>
  );
};
